import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { PermissionsService } from '@fc-core/services/permissions.service';
import { PermissionState } from '@fc-core';
import { DialogContentComponent } from '@fc-shared/ui/dialog-opener/dialog-content.component';
import { DialogHeaderComponent } from '@fc-shared/ui/dialog-opener/dialog-header.component';
import { DialogFooterComponent } from '@fc-shared/ui/dialog-opener/dialog-footer.component';
import { ToggleComponent } from '@fc-shared/ui/toggle/toggle';
import { ToggleButtonModule } from '@fc-shared/ui/toggle-button/toggle-button.module';
import {
  FeatureFlagService,
  FeatureOverrides,
} from '@fc-core/feature-flags/feature-flag.service';
import {
  FEATURE_KEYS,
  FeatureKey,
  getFeatureLabel,
} from '@fc-core/feature-flags/feature.model';

type FlagChoice = 'off' | 'default' | 'on';

// Removed on the backend (moved to feature flags) but still present in the
// localStorage permissions of anyone who toggled them before; saving the
// dialog rewrites the list and purges them for good.
const REMOVED_PERMISSION_CODENAMES = [
  'tracker_reading_can_view_signal_strength',
  'tracker_reading_can_view_delay_in_seconds',
  'tracker_reading_can_view_battery_charts',
];

// Still valid on the backend, just not useful to toggle here; kept out of the
// dialog but merged back on save so their stored state survives.
const HIDDEN_PERMISSION_CODENAMES = [
  'accounting_customer_api_view_customer',
  'accounting_customer_can_switch_customer_view',
  'accounting_customer_change_customer',
];

@Component({
  selector: 'fc-developer-options',
  template: `
    <fc-dialog-header title="Developer options"></fc-dialog-header>
    <fc-dialog-content>
      @if (permissions.length) {
        <div class="options gap-12 p-20">
          @for (permission of permissions; track permission) {
            <fc-toggle [(checked)]="permission.active">
              {{ permission.name }}
            </fc-toggle>
          }
        </div>
      }

      <div class="px-20 py-20">
        <div class="options gap-8">
          @for (key of featureKeys; track key) {
            <div class="flex items-center justify-between gap-12">
              <div class="flex flex-col">
                <span class="text-sm font-medium">{{
                  getFeatureLabel(key)
                }}</span>
                <span class="text-xs text-secondary-400">{{ key }}</span>
              </div>
              <fc-toggle-button-group
                [value]="flagChoices[key]"
                (changed)="setFlagChoice(key, $event)"
              >
                <fc-toggle-button value="off">Off</fc-toggle-button>
                <fc-toggle-button value="default">Default</fc-toggle-button>
                <fc-toggle-button value="on">On</fc-toggle-button>
              </fc-toggle-button-group>
            </div>
          }
        </div>
      </div>
    </fc-dialog-content>

    <fc-dialog-footer
      [primaryText]="'Save'"
      (primaryClicked)="save()"
    ></fc-dialog-footer>
  `,
  styles: [
    `
      .options {
        display: flex;
        flex-direction: column;
      }
    `,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogHeaderComponent,
    DialogContentComponent,
    DialogFooterComponent,
    ToggleComponent,
    ToggleButtonModule,
  ],
})
export class DeveloperOptionsComponent {
  private permissionsService = inject(PermissionsService);
  private flags = inject(FeatureFlagService);

  readonly featureKeys = [...FEATURE_KEYS].sort();
  readonly getFeatureLabel = getFeatureLabel;
  private hiddenPermissions: PermissionState[] = (
    this.permissionsService.userPermissions || []
  ).filter((permission) =>
    HIDDEN_PERMISSION_CODENAMES.includes(permission.fullCodename),
  );
  permissions: PermissionState[] = (
    this.permissionsService.userPermissions || []
  ).filter(
    (permission) =>
      !REMOVED_PERMISSION_CODENAMES.includes(permission.fullCodename) &&
      !HIDDEN_PERMISSION_CODENAMES.includes(permission.fullCodename),
  );
  flagChoices = Object.fromEntries(
    FEATURE_KEYS.map((key) => {
      const override = this.flags.overrideFor(key);
      return [
        key,
        override === undefined ? 'default' : override ? 'on' : 'off',
      ];
    }),
  ) as Record<FeatureKey, FlagChoice>;

  setFlagChoice(key: FeatureKey, choice: FlagChoice): void {
    this.flagChoices[key] = choice;
  }

  save() {
    const overrides: FeatureOverrides = {};
    for (const key of FEATURE_KEYS) {
      if (this.flagChoices[key] !== 'default') {
        overrides[key] = this.flagChoices[key] === 'on';
      }
    }
    // The page reload below applies the overrides (read once per load).
    this.flags.writeOverrides(overrides);
    this.permissionsService.updatePermissions([
      ...this.permissions,
      ...this.hiddenPermissions,
    ]);
    location.assign('/map');
  }
}
